import { asDate } from "@@/shared/date-time/as-date";
import type { FullDate } from "@@/shared/date-time/full-date";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import type { WeekdayShortName } from "@@/shared/parse-weekly-schedule";
import type { LanguageCode } from "@@/shared/translations/language-code";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import { enGB as enLocale, sv as svLocale } from "date-fns/locale";
import { getLocaleFromLanguageCode } from "./get-locale-from-language-code";

/**
 * E.g with default locale (enLocale)
 * Mon, Tue, Wed....
 * @returns
 */
function getWeekdayShortNameInEnglish(
    date: IsoAndUnixTimestamp,
): WeekdayShortName;
function getWeekdayShortNameInEnglish(date: Date): WeekdayShortName;
function getWeekdayShortNameInEnglish(date: FullDate): WeekdayShortName;
function getWeekdayShortNameInEnglish(
    date: Date | FullDate | IsoAndUnixTimestamp,
): WeekdayShortName {
    const _date = asDate(date);
    if (!_date) throw new Error(`invalid date; ${date}`);
    return format(_date, "E", { locale: enLocale }) as WeekdayShortName;
}

function getWeekdayShortName(date: IsoAndUnixTimestamp | Date): string;
function getWeekdayShortName(
    date: IsoAndUnixTimestamp | Date,
    languageCode: LanguageCode,
): string;
function getWeekdayShortName(
    date: IsoAndUnixTimestamp | Date,
    locale: Locale,
): string;
function getWeekdayShortName(
    date: IsoAndUnixTimestamp | Date,
    localization: Locale | LanguageCode = svLocale,
): string {
    const locale =
        typeof localization === "string"
            ? getLocaleFromLanguageCode(localization)
            : localization;
    return format(asDate(date), "E", { locale });
}

const getWeekdayShortNameFromDate = (date: Date, locale: Locale = enLocale) => {
    return format(date, "E", { locale });
};
const getWeekdayShortNameInEnglishFromDate = (date: Date): WeekdayShortName => {
    return format(date, "E", {
        locale: enLocale,
    }).toLowerCase() as WeekdayShortName;
};

const getWeekdayShortNameInSwedishFromDate = (date: Date) => {
    return getWeekdayShortName(date, svLocale);
};
const getMonthName = (date: Date, languageCode: LanguageCode = "sv") => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "MMMM", { locale });
};
const getMonthShortName = (date: Date, languageCode: LanguageCode = "sv") => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "MMM", { locale });
};
const getMonthShortNameInSwedishFromDate = (date: Date) => {
    return getMonthShortName(date, "sv");
};

const getMonthNameInSwedishFromDate = (date: Date) => {
    return getMonthName(date, "sv");
};

const getShortDateWeekDayMonth = (
    date: Date,
    languageCode: LanguageCode = "sv",
) => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "E d MMM", { locale });
};
const getShortDateMonth = (date: Date, languageCode: LanguageCode = "sv") => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "d MMM", { locale });
};
const getShortDateDayMonthYear = (
    date: Date,
    languageCode: LanguageCode = "sv",
) => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "d MMM yyyy", { locale });
};

const getLongDateWeekDayMonth = (
    date: Date,
    languageCode: LanguageCode = "sv",
) => {
    const locale = getLocaleFromLanguageCode(languageCode);
    return format(date, "EEEE d MMM", { locale });
};

/**
 * E.g with default locale (sv)
 * Måndag, Tisdag, Onsdag....
 *
 * @param {Date} date
 * @param {Locale} [locale=sv]
 * @returns
 */
function getNameOfDayFromDate(date: Date): string;
function getNameOfDayFromDate(date: Date, languageCode: LanguageCode): string;
function getNameOfDayFromDate(date: Date, locale: Locale): string;
function getNameOfDayFromDate(
    date: Date,
    localization: Locale | LanguageCode = svLocale,
) {
    const locale =
        typeof localization === "string"
            ? getLocaleFromLanguageCode(localization)
            : localization;
    return format(date, "EEEE", { locale });
}
/**
 * E.g with default locale (sv)
 * Måndag, Tisdag, Onsdag....
 *
 * @param {IsoAndUnixTimestamp} date
 * @param {Locale} [locale=sv]
 * @returns
 */
function getNameOfDay(date: IsoAndUnixTimestamp): string;
function getNameOfDay(
    date: IsoAndUnixTimestamp,
    languageCode: LanguageCode,
): string;
function getNameOfDay(date: IsoAndUnixTimestamp, locale: Locale): string;
function getNameOfDay(
    date: IsoAndUnixTimestamp,
    localization: Locale | LanguageCode = svLocale,
): string {
    const locale =
        typeof localization === "string"
            ? getLocaleFromLanguageCode(localization)
            : localization;

    return getNameOfDayFromDate(asDate(date), locale);
}

export {
    getLongDateWeekDayMonth,
    getMonthName,
    getMonthNameInSwedishFromDate,
    getMonthShortName,
    getMonthShortNameInSwedishFromDate,
    getNameOfDay,
    getNameOfDayFromDate,
    getShortDateDayMonthYear,
    getShortDateMonth,
    getShortDateWeekDayMonth,
    getWeekdayShortName,
    getWeekdayShortNameFromDate,
    getWeekdayShortNameInEnglish,
    getWeekdayShortNameInEnglishFromDate,
    getWeekdayShortNameInSwedishFromDate,
};
